import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calendar, Clock, User, Mail, Phone, Scissors, 
  CheckCircle, XCircle, AlertCircle, Eye, Trash2,
  LogOut, RefreshCw, Search, Filter, ChevronDown,
  DollarSign, Users, CalendarDays, TrendingUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending_payment: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending Payment' },
  payment_uploaded: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Payment Uploaded' },
  confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Confirmed' },
  completed: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Completed' },
  cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' },
  no_show: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'No Show' },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-surface border border-gold/20 rounded-xl p-6">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-[#A3A3A3] text-sm font-sans">{label}</p>
        <p className="text-2xl font-bold text-white font-serif">{value}</p>
      </div>
    </div>
  </div>
);

const BookingCard = ({ booking, onUpdate, onViewPayment, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [adminNotes, setAdminNotes] = useState(booking.admin_notes || '');
  const status = statusColors[booking.status] || statusColors.pending_payment;

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${booking.id}`, { status: newStatus });
      toast.success(`Booking ${newStatus === 'confirmed' ? 'confirmed' : 'updated'}!`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.patch(`${API}/bookings/${booking.id}`, { admin_notes: adminNotes });
      toast.success('Notes saved!');
      onUpdate();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  return (
    <motion.div
      layout
      className="bg-surface border border-gold/20 rounded-xl overflow-hidden"
    >
      <div 
        className="p-4 cursor-pointer hover:bg-gold/5 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${status.bg.replace('/20', '')}`} />
            <div>
              <h3 className="font-serif text-lg text-white">{booking.name}</h3>
              <p className="text-sm text-[#A3A3A3]">{booking.service_label}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-gold font-serif">{booking.date?.split(',')[1]?.trim() || booking.date}</p>
              <p className="text-sm text-pink">{booking.time}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
              {status.label}
            </span>
            <ChevronDown className={`w-5 h-5 text-gold transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gold/20"
          >
            <div className="p-4 space-y-4">
              {/* Contact Info */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gold" />
                  <span className="text-[#A3A3A3] text-sm">{booking.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gold" />
                  <a href={`https://wa.me/63${booking.phone?.substring(1)}`} target="_blank" rel="noopener noreferrer" className="text-green-400 text-sm hover:underline">
                    {booking.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-gold" />
                  <span className="text-[#A3A3A3] text-sm">Booked: {new Date(booking.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Message */}
              {booking.message && (
                <div className="bg-[#0a0a0a] rounded-lg p-3">
                  <p className="text-xs text-gold mb-1">Client Notes:</p>
                  <p className="text-[#A3A3A3] text-sm">{booking.message}</p>
                </div>
              )}

              {/* Payment Screenshot */}
              {booking.payment_screenshot && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-400 text-sm">Payment screenshot uploaded</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onViewPayment(booking.payment_screenshot)}
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <Label className="text-gold text-sm">Admin Notes</Label>
                <div className="flex gap-2 mt-1">
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes about this booking..."
                    className="bg-[#0a0a0a] border-gold/30 text-white text-sm resize-none"
                    rows={2}
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveNotes}
                    className="bg-gold text-black hover:bg-gold-light"
                  >
                    Save
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gold/20">
                {booking.status === 'payment_uploaded' && (
                  <Button
                    size="sm"
                    onClick={() => handleStatusChange('confirmed')}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Confirm Booking
                  </Button>
                )}
                {booking.status === 'confirmed' && (
                  <Button
                    size="sm"
                    onClick={() => handleStatusChange('completed')}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark Completed
                  </Button>
                )}
                {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange('cancelled')}
                      className="border-red-500 text-red-400 hover:bg-red-500/20"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange('no_show')}
                      className="border-gray-500 text-gray-400 hover:bg-gray-500/20"
                    >
                      No Show
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDelete(booking.id)}
                  className="border-red-500/50 text-red-400/50 hover:bg-red-500/10 ml-auto"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsLoggedIn(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/admin/stats`)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('admin_token', res.data.token);
      setIsLoggedIn(true);
      toast.success('Welcome back!');
      fetchData();
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setBookings([]);
    setStats(null);
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${id}`);
      toast.success('Booking deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const filteredBookings = bookings.filter(b => {
    const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
    const matchesSearch = searchQuery === '' || 
      b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.phone?.includes(searchQuery);
    return matchesStatus && matchesSearch;
  });

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface border border-gold/30 rounded-2xl p-8 max-w-md w-full"
        >
          <h1 className="font-script text-4xl text-gold text-center mb-2">BraidsByMel</h1>
          <p className="text-[#A3A3A3] text-center mb-8">Admin Dashboard</p>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-gold">Username</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label className="text-gold">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                placeholder="Enter password"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gold text-black hover:bg-pink hover:text-white font-serif py-6"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-script text-4xl text-gold">Admin Dashboard</h1>
            <p className="text-[#A3A3A3]">Manage your bookings</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchData}
              variant="outline"
              className="border-gold text-gold hover:bg-gold/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500 text-red-400 hover:bg-red-500/20"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Total Bookings" value={stats.total_bookings} color="bg-gold" />
            <StatCard icon={AlertCircle} label="Awaiting Payment" value={stats.pending_payment + stats.payment_uploaded} color="bg-blue-500" />
            <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed} color="bg-green-500" />
            <StatCard icon={TrendingUp} label="Completed" value={stats.completed} color="bg-purple-500" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or phone..."
              className="bg-surface border-gold/30 text-white pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending_payment', 'payment_uploaded', 'confirmed', 'completed', 'cancelled'].map(status => (
              <Button
                key={status}
                onClick={() => setFilterStatus(status)}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                className={filterStatus === status 
                  ? 'bg-gold text-black' 
                  : 'border-gold/30 text-[#A3A3A3] hover:text-gold'}
              >
                {status === 'all' ? 'All' : statusColors[status]?.label || status}
              </Button>
            ))}
          </div>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
              <p className="text-[#A3A3A3]">No bookings found</p>
            </div>
          ) : (
            filteredBookings.map(booking => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onUpdate={fetchData}
                onViewPayment={(filename) => setSelectedImage(`${API}/uploads/${filename}`)}
                onDelete={handleDeleteBooking}
              />
            ))
          )}
        </div>
      </div>

      {/* Payment Screenshot Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedImage}
              alt="Payment Screenshot"
              className="max-w-full max-h-[90vh] rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Admin;
